/**
 * Lookup Service
 * Fetches lookup data (vehicle usage, night parking, vehicle financing)
 * from the .NET Lookup API. Results are cached after the first fetch.
 */

import { apiFetch } from "../utils/api";

const BASE = "/Lookup";

export interface LookupItem {
    id: number;
    nameAr: string;
    nameEn: string;
}

export interface LookupData {
    vehicleUsage: LookupItem[];
    nightParkingLocation: LookupItem[];
    vehicleFinancing: LookupItem[];
}

export interface UnderwritingQuestionOption {
    value: string;
    labelAr: string;
    labelEn: string;
}

export type UnderwritingQuestionUiType = "choice" | "number";

export interface UnderwritingQuestion {
    id: string;
    flowStep: string; // must match FlowStep string in FE
    uiType: UnderwritingQuestionUiType;
    titleAr: string;
    titleEn: string;
    noteAr?: string | null;
    noteEn?: string | null;
    placeholderAr?: string | null;
    placeholderEn?: string | null;
    options: UnderwritingQuestionOption[];
}

async function fetchLookup(endpoint: string): Promise<LookupItem[]> {
    const response = await apiFetch(`${BASE}/${endpoint}`, { method: "GET" });

    if (!response.ok) {
        console.error(`[LookupService] Failed to fetch ${endpoint}: ${response.status}`);
        return [];
    }

    const data = await response.json();
    return (data || []).map((item: any) => ({
        id: item.id,
        nameAr: item.nameAr || "",
        nameEn: item.nameEn || "",
    }));
}

/**
 * Fetches all three lookup types in parallel.
 * Returns empty arrays for any that fail (graceful degradation).
 */
export async function fetchAllLookups(): Promise<LookupData> {
    const [vehicleUsage, nightParkingLocation, vehicleFinancing] = await Promise.all([
        fetchLookup("vehicle-usage"),
        fetchLookup("night-parking-location"),
        fetchLookup("vehicle-financing"),
    ]);
    return { vehicleUsage, nightParkingLocation, vehicleFinancing };
}

export async function fetchUnderwritingQuestions(): Promise<UnderwritingQuestion[]> {
    const response = await apiFetch(`${BASE}/underwriting-questions`, { method: "GET" });

    if (!response.ok) {
        console.error(`[LookupService] Failed to fetch underwriting-questions: ${response.status}`);
        return [];
    }

    const data = await response.json();
    return (data || []).map((q: any) => ({
        id: String(q.id || ""),
        flowStep: String(q.flowStep || ""),
        uiType: (q.uiType === "number" ? "number" : "choice") as UnderwritingQuestionUiType,
        titleAr: q.titleAr || "",
        titleEn: q.titleEn || "",
        noteAr: q.noteAr ?? null,
        noteEn: q.noteEn ?? null,
        placeholderAr: q.placeholderAr ?? null,
        placeholderEn: q.placeholderEn ?? null,
        options: (q.options || []).map((opt: any) => ({
            value: String(opt.value ?? ""),
            labelAr: opt.labelAr || "",
            labelEn: opt.labelEn || "",
        })),
    })) as UnderwritingQuestion[];
}
